import { BUTTON_LABELS, COMMON_TEXT } from '@/constants';
import { Button, PrintButton } from '@/components/Buttons';
import { cn } from '@/utils';
import { PrintLogo } from '@/components/Images';
import { SimulationScoreProps } from '../SimulationScore/types';
import { TextContent, TextSplitMap } from '@/components/Text';
import { useState } from 'react';

/** Props for the SimulationScoreHeader component */
interface SimulationScoreHeaderProps {
  /** Title of the certification */
  certificationTitle?: string;
  /** Date when the certification was created */
  createdDate?: string;
  /** Full name of the current user */
  currentUserFullName: string;
  /** Handler for print button click */
  handlePrintButtonClick: () => void;
  /** Flag to indicate loading state for the header */
  isLoading: SimulationScoreProps['isLoading'];
  /** Flag indicating if the scorecard is currently being printed */
  isPrinting: SimulationScoreProps['isPrinting'];
  /** Flag to determine if the screen size is extra large or larger */
  isXlUp: boolean;
  /** Function to navigate back to the dashboard */
  navigateToDashboard: () => void;
  /** Optional subtitle text for the page */
  pageSubtitleText: string | null;
  /** Title text for the page */
  pageTitleText: string;
  /** Formatted date string for printing */
  printDate: string;
  /** Ref for the printable content */
  printRef: React.RefObject<HTMLDivElement>;
  /** The score achieved in the simulation, if available */
  score: SimulationScoreProps['score'];
  /** Flag to indicate if the score has been processed and is ready for display */
  scoreIsProcessed: SimulationScoreProps['scoreIsProcessed'];
  /** The title of the simulation, used for the print document title */
  simulationTitle?: string;
}

/**
 * SimulationScoreHeader is a component that renders the header section of the simulation scorecard.
 * It includes the certification title, page title, optional subtitle, and actions such as
 * returning to the dashboard and printing the scorecard. The header adjusts its layout and
 * content based on the loading state, printing state, and screen size.
 */
export const SimulationScoreHeader: React.FC<SimulationScoreHeaderProps> = ({
  certificationTitle,
  createdDate,
  currentUserFullName,
  handlePrintButtonClick,
  isLoading = false,
  isPrinting = false,
  isXlUp,
  navigateToDashboard,
  pageSubtitleText,
  pageTitleText,
  printDate,
  printRef,
  score,
  scoreIsProcessed,
  simulationTitle,
}) => {
  const [returningToDashboard, setReturningToDashboard] = useState(false);
  const handleReturnToDashboardClick = () => {
    setReturningToDashboard(true);
    navigateToDashboard();
  };

  // ===============================================================
  // Subcomponents
  // ===============================================================

  /**
   * Renders the certification title and action buttons. The layout adjusts when
   * printing to reduce spacing. The "Return to Dashboard" button is hidden when printing.
   */
  const CertificationTitleAndActions = ({ loading }: { loading: boolean }) => {
    const classNames = cn('mb-6 flex w-full items-center justify-between', {
      'mb-1': isPrinting, // Reduce bottom margin when printing to save space
    });

    return (
      <div className={classNames} data-testid="certification-title-and-actions">
        <TextContent
          className="!font-display text-body-title font-bold text-neutral-gray-800"
          isLoading={isLoading}
          label={certificationTitle}
          loadingClassName="w-1/3 h-[1.25rem]"
          testId="certification-title"
          type="paragraph"
        />

        {!isPrinting && (
          <div className="flex items-center gap-3">
            <Button
              className="text-neutral-black [&_svg]:text-brand-primary"
              iconRight="ArrowRight"
              isTransitioning={loading}
              label={BUTTON_LABELS.RETURN_TO_DASHBOARD}
              onClick={handleReturnToDashboardClick}
              variant="tertiary"
            />
          </div>
        )}
      </div>
    );
  };

  /**
   * Determine the page title styling and content based on loading and printing states.
   * When printing, use a more compact style and include the user's name and print date.
   */
  const PageTitle = () => {
    const classNames = isPrinting
      ? 'text-section-title leading-section-title'
      : 'text-page-title leading-page-title';

    return (
      <div className="mb-3" data-testid="page-title-container">
        <TextContent
          className={classNames}
          isLoading={isLoading}
          label={pageTitleText}
          level={1}
          loadingClassName={cn('w-full h-[4rem]', isLoading ? (isXlUp ? '' : 'mt-0.5') : '')}
          loadingLines={isXlUp ? 1 : 2}
          testId="page-title"
          type="headline"
          variant="pageTitle"
        />

        {isPrinting && (
          <p className="mt-1 text-base font-bold text-neutral-gray-700">{currentUserFullName}</p>
        )}
        {isPrinting && createdDate && (
          <p className="font-text text-sm text-neutral-gray-700">{printDate}</p>
        )}
      </div>
    );
  };

  /**
   * Renders the page subtitle if provided. The subtitle can include dynamic information
   * based on the score and simulation context, and is styled to differentiate it from the
   * main title. It is hidden when there is no subtitle text to display.
   */
  const PageSubtitle = () => {
    if (!pageSubtitleText) return null;
    return (
      <div
        className="mb-3 grid grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-6"
        data-testid="page-subtitle"
      >
        <div className="col-span-12 lg:col-span-9">
          <div className="typography-body-title font-text">
            <TextSplitMap text={pageSubtitleText} />
          </div>
        </div>
      </div>
    );
  };

  /**
   * Renders the print button for the scorecard. The button is only shown when the score has
   * been processed, there is a valid score, and the scorecard is not currently in the printing
   * state. This ensures that users can only print the scorecard when it is ready and prevents
   * multiple print actions while already printing.
   */
  const ScorePrintButton = () => {
    if (!scoreIsProcessed || !score || isPrinting) return null;
    return (
      <PrintButton
        contentRef={printRef}
        documentTitle={`${simulationTitle || COMMON_TEXT.SCORECARD} — ${currentUserFullName}`}
        onClick={handlePrintButtonClick}
      />
    );
  };

  // ===============================================================
  // Main render
  // ===============================================================
  return (
    <div className="mb-8 w-full" data-testid="simulation-score-header">
      <PrintLogo isPrinting={isPrinting} />
      <CertificationTitleAndActions loading={returningToDashboard} />
      <PageTitle />
      <PageSubtitle />
      <ScorePrintButton />
    </div>
  );
};

SimulationScoreHeader.displayName = 'SimulationScoreHeader';

export default SimulationScoreHeader;
